import Vue from "vue";
import messengerAPI from "../../api/messengerAPI";
import messengerSocket from "../../socket/messengerSocket";

const DEFAULT_MSG_QUERY = 30;

const state = {
  /*
    The messages object has the following form
    {
      CID: [
        {
          msg: {String}
          date: {Date}
          senderID: {String}
        }
      ] 
    }
    CID is the conversation ID
    sender is either 0 if currentUser sent, 1 if the partner sent
    and 2 for 'this person accepted your connect request'
    The array for each user is sorted such that 
    the newest messages are at the end of the array
  */
  messages: {},

  /*
    The conversations object has the following form
    {
      CID: {
        partner: {
          ID: {String} partnerID,
          displayName: {String} displayName,
          profileImageUrl: {String},
          connected: {Boolean}
        },
        messagePreview: {
          preview: {String} the message preview text itself
          date: {Date} the date when the last message was sent
          read: {Boolean} true if current user sent the last messages or unread message count === 0
          unreadCount: {Number} the nr of unread messages
        },
        queryData: {
          lastBatchNr: {Number} the last batch nr to query
          skip: {Number} the next skip parameter to send
          limit: {Number} the next limit query parameter to send
          allLoaded: {Boolean} whether all messages are loaded
          hasLoaded: {Boolean} whether has loaded messages with HTTP request
        },
        newestBatchNr: {Number},
        msgBackup: {String},
      }
    }
  */
  conversations: {},

  currentConversationID: "",

  modalOpened: false,

  /* the last loaded convo date */
  oldestLoadedDateMS: Date.now() + 7 * 24 * 60 * 60 * 1000,

  allConvoPreviewsLoaded: false,
};

const getters = {
  getSortedConversations: (state) => {
    return Object.entries(state.conversations)
      .sort((a, b) => {
        return b[1].messagePreview.date.valueOf() - a[1].messagePreview.date.valueOf();
      })
      .map((x) => {
        x[1].conversationID = x[0];
        return x[1];
      });
  },
  getModalOpenStatus: (state) => {
    return state.modalOpened;
  },
  getCurrentConvoID: (state) => {
    return state.currentConversationID;
  },
  getCurrentConvoQueryData: (state) => {
    const ID = state.currentConversationID;
    return state.conversations[ID] ? state.conversations[ID].queryData : null;
  },
  getCurrentConvoPartnerData: (state) => {
    const ID = state.currentConversationID;
    return state.conversations[ID] ? state.conversations[ID].partner : null;
  },
  getCurrentConvoNewestBatchNr: (state) => {
    const ID = state.currentConversationID;
    return state.conversations[ID] ? state.conversations[ID].newestBatchNr : 0;
  },
  getCountOfLoadedPreviews: (state) => {
    return Object.keys(state.conversations).length;
  },

  getConversationBackup: (state) => (conversationID) => {
    return state.conversations[conversationID] ? state.conversations[conversationID].msgBackup : "";
  },
};

const mutations = {
  /**
   * Adds old messages to the convo
   * @param {String} conversationID the ID of the conversation
   * @param {Array} msgArray the array of messages in the correct form,
   * @param {Boolean} needSorting whether the array must be sorted after concatenating
   *  sorted from oldest (first) to newest (last)
   */
  ADD_OLD_MESSAGE_ARRAY(state, payload) {
    const { conversationID, msgArray, needSorting } = payload;
    if (state.messages[conversationID]) {
      state.messages[conversationID] = msgArray.concat(state.messages[conversationID]);
      console.log("@ADD_OLD_MESSAGE_ARRAY needSorting? ", needSorting, state.messages[conversationID][0].date, msgArray[msgArray.length - 1].date);
      if (needSorting) {
        console.log("SORTING?!?!?!?!?", payload);
        state.messages[conversationID].sort((a, b) => a.date - b.date);
      }
    } else {
      Vue.set(state.messages, conversationID, msgArray);
    }
  },
  /**
   * Adds a conversation to the state
   */
  ADD_CONVERSATION(state, conversation) {
    Vue.set(state.conversations, conversation.conversationID, conversation.conversation);
    const addedDateMS = conversation.conversation.messagePreview.date.valueOf();
    if (addedDateMS < state.oldestLoadedDateMS) {
      state.oldestLoadedDateMS = addedDateMS;
    }
  },
  ADD_NEW_MESSAGE(state, payload) {
    const { conversationID, message } = payload;
    if (state.messages[conversationID]) state.messages[conversationID].push(message);
    else Vue.set(state.messages, conversationID, [message]);
  },
  SET_CURRENT_CONVERSATION(state, newConversationID) {
    state.currentConversationID = newConversationID;
  },
  SET_CONVERSATION_QUERY_DATA(state, payload) {
    const { conversationID, queryData } = payload;
    if (state.conversations[conversationID]) {
      state.conversations[conversationID].queryData = queryData;
    }
  },
  SET_CONVERSATION_PREVIEW(state, payload) {
    const { conversationID, newPreview } = payload;
    state.conversations[conversationID].messagePreview = newPreview;
    if (newPreview.date.valueOf() < state.oldestLoadedDateMS) {
      state.oldestLoadedDateMS = newPreview.date.valueOf();
    }
  },
  SET_NEWEST_BATCH_NR(state, payload) {
    const { conversationID, batchNr } = payload;
    if (state.conversations[conversationID] && state.conversations[conversationID].newestBatchNr < batchNr) {
      state.conversations[conversationID].newestBatchNr = batchNr;
    }
  },
  SET_CONVERSATION_AS_READ(state, conversationID) {
    const convo = state.conversations[conversationID];
    if (convo && convo.messagePreview) {
      convo.messagePreview.read = true;
      convo.messagePreview.unreadCount = 0;
    }
  },
  SET_MSG_BACKUP(state, payload) {
    const { conversationID, backup } = payload;
    if (state.conversations[conversationID]) {
      state.conversations[conversationID].msgBackup = backup;
    }
  },
  SET_MODAL_OPENED(state, openedStatus) {
    state.modalOpened = openedStatus;
    if (!openedStatus) {
      state.currentConversationID = "";
    }
  },
  SET_ALL_CONVOS_LOADED(state, status) {
    state.allConvoPreviewsLoaded = status;
  },
  RESET_CONVERSATION(state) {
    state.currentConversationID = "";
  },
};

const actions = {
  /**
   * @param {String} userID the ID of the current user
   * @param {Array} conversations the array of convos from backend
   */
  addConversations({ state, commit }, payload) {
    const { userID, conversations } = payload;
    for (let i = 0; i < conversations.length; i++) {
      const preview = getPreviewFromConvo(conversations[i], userID);
      const partner = getPartnerFromConvo(conversations[i]);
      const queryData = state.conversations[conversations[i].CID] ? state.conversations[conversations[i].CID].queryData : getDefaultQueryingData();
      commit("ADD_CONVERSATION", {
        conversationID: conversations[i].CID,
        conversation: {
          partner: partner,
          messagePreview: preview,
          queryData: queryData,
          newestBatchNr: 0,
          msgBackup: "",
        },
      });
    }
  },

  /**
   * Cleans up the messages to insert
   */
  cleanOldMessageArray({ state }, { conversationID, msgArray }) {
    // console.log("current state @cleanup", state.messages);
    if (state.messages[conversationID]) {
      const lengthToCheck = Math.min(msgArray.length, state.messages[conversationID].length);
      const needSorting = state.messages[conversationID][0].date <= msgArray[msgArray.length - 1].date;
      // console.log("@cleanup needsorting: ", needSorting, msgArray.length, lengthToCheck);
      if (needSorting) {
        for (let i = msgArray.length; --i >= 1; ) {
          for (let j = 0; j < lengthToCheck; j++) {
            const existing = state.messages[conversationID][j];
            // console.log("@cleaning: msg: old : new", existing.msg, msgArray[i].msg, existing.msg === msgArray[i].msg);
            // console.log("@cleaning: date: old : new", existing.date.valueOf(), msgArray[i].date.valueOf(), existing.date.valueOf() === msgArray[i].date.valueOf());
            // console.log("@cleaning: senderID: old : new", existing.senderID, msgArray[i].senderID, existing.senderID === msgArray[i].senderID);
            if (existing.msg === msgArray[i].msg && existing.date.valueOf() === msgArray[i].date.valueOf() && existing.senderID === msgArray[i].senderID) {
              console.log("@cleaning SPLICED");
              msgArray.splice(i, 1);
              break;
            }
          }
        }
      }
      return {
        msgArray,
        needSorting,
      };
    } else {
      return {
        msgArray,
        needSorting: false,
      };
    }
  },

  /**
   * Adds messages after getting the messages from backend
   * @param {String} conversationID the ID of the conversation
   * @param {Object} resultObject the result object from backend
   * @param {Array} resultObject.UIDs the array of userIDs
   * @param {Number} resultObject.nextBatchNr the next batch nr to send in the next request
   * @param {Number} resultObject.nextSkip the next skip value to send in the next request
   * @param {Number} resultObject.nextLimit the next limit to send in the next request
   * @param {Array} resultObject.messages the messages array
   * @param {Boolean} resultObject.allLoaded whether all results are loaded.
   */
  async addOldMessages({ commit, dispatch }, payload) {
    const { conversationID, resultObject } = payload;
    const messages = resultObject.messages
      .map((x) => {
        const message = {
          msg: x.m,
          date: new Date(x.d),
          senderID: x.s !== 2 ? resultObject.UIDs[x.s] : "general",
          replyTo: x.replyTo,
        };
        if (x.s === 2) {
          message.statusUpdateBy = x.statusUpdateBy;
        }
        return message;
      })
      .sort((a, b) => a.date - b.date);
    console.log("@ADD OLD MESSAGES msgs:", messages);
    const { msgArray, needSorting } = await dispatch("cleanOldMessageArray", {
      conversationID,
      msgArray: messages,
    });
    commit("ADD_OLD_MESSAGE_ARRAY", {
      conversationID,
      msgArray,
      needSorting,
    });
    commit("SET_NEWEST_BATCH_NR", {
      conversationID,
      batchNr: resultObject.nextBatchNr,
    });
    commit("SET_CONVERSATION_QUERY_DATA", {
      conversationID,
      queryData: {
        lastBatchNr: resultObject.nextBatchNr,
        skip: resultObject.nextSkip,
        limit: resultObject.nextLimit,
        allLoaded: resultObject.allLoaded,
        hasLoaded: true,
      },
    });
  },

  /**
   * Stores the received message.
   *
   * 1. checks if conversation exists
   *      if it does not, requests info from db
   * 2. update newest batch and msg preview for the convo
   * 3. add message to the respective array
   *
   * @param {String} CID the conversation ID
   * @param {String} senderID the ID of the user who sent the message
   * @param {String} message the message itself
   * @param {Date} date the date when the message was sent
   * @param {Number} newestBatchNr the current newest batch nr
   * @param {String} currentUserID the ID of the current user
   *
   * @returns {Object} the partner metadata object
   *  ID {String},
   *  displayName {String},
   *  profileImageUrl {String},
   *  connected {Boolean}
   */
  async addNewMessage({ state, dispatch, commit }, messageData) {
    console.log("@addNewMessage data: ", messageData);

    if (!state.conversations[messageData.CID]) {
      const convoExists = await dispatch("requestConvoByID", {
        currentUserID: messageData.currentUserID,
        conversationID: messageData.CID,
      });
      /* exit when convo does not exist */
      if (!convoExists) return false;
    }
    /* at this point we can be sure that the convo exists in vuex */

    const read = messageData.senderID === messageData.currentUserID || state.currentConversationID === messageData.CID;

    const messagePreview = {
      preview: messageData.message,
      date: messageData.date,
      read,
      unreadCount: messageData.senderID === messageData.currentUserID ? 0 : state.conversations[messageData.CID].messagePreview.unreadCount + 1,
    };
    const message = {
      msg: messageData.message,
      date: messageData.date,
      senderID: messageData.senderID,
      replyTo: messageData.replyingTo,
    };

    commit("ADD_NEW_MESSAGE", {
      conversationID: messageData.CID,
      message,
    });
    commit("SET_CONVERSATION_PREVIEW", {
      conversationID: messageData.CID,
      newPreview: messagePreview,
    });
    commit("SET_NEWEST_BATCH_NR", {
      conversationID: messageData.CID,
      batchNr: messageData.newestBatchNr,
    });
    const queryData = state.conversations[messageData.CID].queryData;
    if (!queryData.hasLoaded) {
      /* 
        if has only received messages with socket and has not actually opened convo 
        and loaded any messages, update query data to try to exclude requesting already 
        available messages
      */
      const lastBatchNr = queryData.lastBatchNr >= 0 ? queryData.lastBatchNr : messageData.newestBatchNr;
      commit("SET_CONVERSATION_QUERY_DATA", {
        conversationID: messageData.CID,
        queryData: {
          lastBatchNr,
          skip: queryData.skip - 1,
          limit: queryData.limit,
          allLoaded: false,
          hasLoaded: false,
        },
      });
    }
    return state.conversations[messageData.CID].partner;
  },

  async setCurrentConversation({ state, commit, rootGetters, dispatch }, conversationID) {
    const convo = state.conversations[conversationID];
    let convoExists = true;
    if (!convo || !convo.messagePreview) {
      /* if convo not available, load it from db */
      convoExists = await dispatch("requestConvoByID", {
        currentUserID: rootGetters["auth/userID"],
        conversationID,
      });
    }
    commit("SET_CURRENT_CONVERSATION", conversationID);
    if (convoExists && convo && convo.messagePreview) {
      if (!convo.messagePreview.read || convo.messagePreview.unreadCount > 0) {
        messengerSocket.setConvoAsRead(conversationID);
      }
    }
  },

  /**
   * @param {String} currentUserID the current user ID
   * @param {String} conversationID the ID of the convo
   * @returns {Boolean} whether the convo request to DB was successful or not
   */
  async requestConvoByID({ dispatch }, payload) {
    const { conversationID, currentUserID } = payload;
    /* if the convo is not already in vuex, request info and add to vuex */
    console.log("conversation does not exist!");
    if (!conversationID || !currentUserID) return false;
    const convoPreviewData = await messengerAPI.getConversationByID(conversationID);
    if (!convoPreviewData || !convoPreviewData.success) {
      console.error("Something went wrong");
      return false;
    }
    await dispatch("addConversations", {
      userID: currentUserID,
      conversations: [convoPreviewData],
    });
    return true;
  },

  resetConversation({ commit }) {
    commit("RESET_CONVERSATION");
  },
};

function getDefaultQueryingData() {
  return {
    lastBatchNr: -1,
    skip: -DEFAULT_MSG_QUERY,
    limit: DEFAULT_MSG_QUERY,
    allLoaded: false,
    hasLoaded: false,
  };
}

function getPartnerFromConvo(conversation) {
  return {
    ID: conversation.UID,
    displayName: conversation.name,
    profileImageUrl: conversation.url ? conversation.url : null,
    connected: conversation.status === "connected",
  };
}

/**
 * Helper function to transform the preview from backend to the preview format here in frontend
 */
function getPreviewFromConvo(conversation, userID) {
  if (!conversation.messages) {
    return {
      preview: "You connected!",
      date: new Date(Number(conversation.lastDateMS)),
      read: true,
      unreadCount: 0,
    };
  }
  if (conversation.messages.sender === userID) {
    return {
      preview: "You: " + conversation.messages.preview,
      date: new Date(Number(conversation.lastDateMS)),
      read: true,
      unreadCount: 0,
    };
  } else {
    return {
      preview: conversation.messages.preview,
      date: new Date(Number(conversation.lastDateMS)),
      read: !conversation.messages.unreadCount,
      unreadCount: conversation.messages.unreadCount,
    };
  }
}

const messages = {
  namespaced: true,

  state,
  getters,
  mutations,
  actions,
};

export default messages;
